import { Check } from 'lucide-react';
import { cn } from '@pixlide/ui';

/** Étapes du parcours de diffusion, dans l'ordre de la maquette. */
const DIFFUSION_STEPS = ['Configuration', 'Validation', 'Diffusion'] as const;

/**
 * Fil d'étapes « Configuration > Validation > Diffusion ».
 *
 * Purement informatif : on n'y navigue pas, l'avancée se fait par les boutons du
 * formulaire, qui seuls savent si l'étape courante est valide.
 */
export function DiffusionStepper({ current }: { current: number }) {
  return (
    <ol aria-label="Étapes de la diffusion" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-base sm:text-lg">
      {DIFFUSION_STEPS.map((label, index) => {
        const isDone = index < current;
        const isCurrent = index === current;

        return (
          <li key={label} className="flex items-center gap-3" aria-current={isCurrent ? 'step' : undefined}>
            {index > 0 ? (
              <span aria-hidden className="text-ink-500">
                &gt;
              </span>
            ) : null}
            <span className={cn('flex items-center gap-2', isDone || isCurrent ? 'text-ink-900' : 'text-ink-500')}>
              {isDone ? (
                <span className="grid size-6 place-items-center rounded-full bg-ink-900 text-white">
                  <Check aria-hidden className="size-3.5" strokeWidth={3} />
                  <span className="sr-only">Terminée :</span>
                </span>
              ) : (
                <span
                  aria-hidden
                  className={cn(
                    'grid size-6 place-items-center rounded-full text-xs ring-1',
                    isCurrent ? 'font-semibold ring-ink-900' : 'ring-ink-300',
                  )}
                >
                  {index + 1}
                </span>
              )}
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

interface SegmentedControlProps<T extends string> {
  readonly label: string;
  readonly value: T;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  readonly onChange: (value: T) => void;
}

/** Choix exclusif en pastilles (« Une date » / « Une période »), exposé comme un groupe radio. */
export function SegmentedControl<T extends string>({ label, value, options, onChange }: SegmentedControlProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-pill bg-ink-50 p-1 ring-1 ring-ink-100">
      {options.map((option) => {
        const isSelected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-10 min-w-32 rounded-pill px-5 text-sm transition',
              isSelected ? 'bg-white font-medium text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

interface SwitchProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: string;
  readonly disabled?: boolean;
}

/** Interrupteur on/off : un `role="switch"`, annoncé « activé / désactivé » par les lecteurs d'écran. */
export function Switch({ checked, onChange, label, disabled = false }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-7 w-12 shrink-0 rounded-pill transition-colors disabled:cursor-not-allowed',
        checked ? 'bg-ink-900' : 'bg-ink-100',
        disabled && !checked ? 'opacity-60' : null,
      )}
    >
      <span
        aria-hidden
        className={cn(
          // `left-0` explicite : sans lui, un enfant absolu d'un <button> prend sa position
          // « statique », centrée par le bouton, et la translation le pousse hors du rail.
          'absolute top-1 left-0 size-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  );
}
